import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { messagesAPI, offersAPI, servicesAPI, marketplaceAPI, api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Send, Loader2, MessagesSquare, ArrowLeft, FileText, Paperclip, X } from "lucide-react";
import OfferMessageCard from "@/components/OfferMessageCard";

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => { const r = new FileReader(); r.onload = () => resolve(r.result); r.onerror = reject; r.readAsDataURL(file); });

export default function Messages() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [messages, setMessages] = useState([]);
  const [convo, setConvo] = useState(null);
  const [loadingConvo, setLoadingConvo] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [offersById, setOffersById] = useState({});
  const [txByOfferId, setTxByOfferId] = useState({});
  const [serviceListing, setServiceListing] = useState(null);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteAmount, setQuoteAmount] = useState("");
  const [quoteMsg, setQuoteMsg] = useState("");
  const [quoteFile, setQuoteFile] = useState(null);
  const [quoteBusy, setQuoteBusy] = useState(false);
  const endRef = useRef(null);

  const loadOffers = useCallback(async (convoId) => {
    try {
      const r = await offersAPI.forConversation(convoId);
      const list = r.data?.offers || [];
      const byId = {};
      for (const o of list) byId[o.id] = o;
      setOffersById(byId);
      // Also load user transactions to map to offers (buy-now needs a transaction id)
      try {
        const tx = await api.get("/offers/transactions");
        const map = {};
        for (const t of (tx.data?.transactions || [])) {
          if (t.offer_id) map[t.offer_id] = t;
        }
        setTxByOfferId(map);
      } catch { /* ignore */ }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    messagesAPI
      .getConversations({ limit: 50 })
      .then((res) => setConversations(res.data?.conversations || []))
      .catch(() => setConversations([]))
      .finally(() => setLoadingList(false));
  }, []);

  useEffect(() => {
    if (!id) { setMessages([]); setConvo(null); setOffersById({}); setTxByOfferId({}); setServiceListing(null); return; }
    setLoadingConvo(true);
    messagesAPI
      .getConversation(id, { limit: 100 })
      .then((res) => {
        setConvo(res.data?.conversation);
        setMessages(res.data?.messages || []);
        const lid = res.data?.conversation?.listing_id;
        if (lid) {
          marketplaceAPI.getListing(lid).then((r) => setServiceListing(r.data)).catch(() => setServiceListing(null));
        } else {
          setServiceListing(null);
        }
      })
      .catch(() => toast.error("Could not load conversation"))
      .finally(() => setLoadingConvo(false));
    loadOffers(id);
  }, [id, loadOffers]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async () => {
    if (!text.trim() || !id) return;
    setSending(true);
    try {
      await messagesAPI.sendMessage(id, text);
      setText("");
      const res = await messagesAPI.getConversation(id, { limit: 100 });
      setMessages(res.data?.messages || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send");
    } finally {
      setSending(false);
    }
  };

  const refreshAfterOfferChange = async () => {
    if (!id) return;
    await loadOffers(id);
    const res = await messagesAPI.getConversation(id, { limit: 100 });
    setMessages(res.data?.messages || []);
  };

  const isServiceSeller =
    serviceListing?.listing_kind === "service" && serviceListing?.seller_id === user?.id;
  const buyerId = (convo?.participants || []).find((p) => p !== user?.id)
    || Object.keys(convo?.participant_usernames || {}).find((uid) => uid !== user?.id);

  const sendQuote = async () => {
    const amt = Number(quoteAmount);
    if (!amt || amt <= 0) { toast.error("Enter a quote amount"); return; }
    setQuoteBusy(true);
    try {
      let attachment_url = "", attachment_name = "";
      if (quoteFile) { attachment_url = await fileToBase64(quoteFile); attachment_name = quoteFile.name; }
      await servicesAPI.sendQuote({
        listing_id: serviceListing.id,
        buyer_id: buyerId,
        amount: amt,
        message: quoteMsg || undefined,
        attachment_url,
        attachment_name,
      });
      setQuoteOpen(false); setQuoteAmount(""); setQuoteMsg(""); setQuoteFile(null);
      toast.success("Quote sent");
      await refreshAfterOfferChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send quote");
    } finally {
      setQuoteBusy(false);
    }
  };

  const otherName = (c) =>
    c.other_user?.username ||
    Object.entries(c.participant_usernames || {}).find(([uid]) => uid !== user?.id)?.[1] ||
    "Conversation";

  return (
    <div className="grid h-[calc(100vh-11rem)] grid-cols-1 gap-4 md:grid-cols-[320px_1fr]">
      {/* Conversation list */}
      <Card className={`overflow-y-auto border-border bg-card ${id ? "hidden md:block" : "block"}`}>
        <div className="border-b border-border p-4">
          <h1 className="font-display text-[1.7rem] font-bold uppercase leading-none">Messages</h1>
        </div>
        {loadingList ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : conversations.length ? (
          conversations.map((c) => (
            <button
              key={c.id}
              onClick={() => navigate(`/messages/${c.id}`)}
              className={`flex w-full items-center gap-3 border-b border-border p-3 text-left transition-colors hover:bg-secondary/40 ${id === c.id ? "bg-secondary/60" : ""}`}
              data-testid={`convo-item-${c.id}`}
            >
              <Avatar className="h-10 w-10"><AvatarFallback className="bg-secondary">{otherName(c).slice(0, 2).toUpperCase()}</AvatarFallback></Avatar>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <p className="truncate font-medium">{otherName(c)}</p>
                  {c.context_type === "sponsorship" && (
                    <Badge className="shrink-0 border-0 bg-primary/20 px-1.5 py-0 text-[10px] text-primary" data-testid={`convo-sponsor-badge-${c.id}`}>Sponsorship</Badge>
                  )}
                </div>
                {c.context_type === "sponsorship" && c.sponsorship_meta && (
                  <p className="truncate text-[11px] text-muted-foreground">
                    @{c.sponsorship_meta.username}
                    {c.sponsorship_meta.tier ? ` · ${c.sponsorship_meta.tier}` : ""}
                    {c.sponsorship_meta.status ? ` · ${c.sponsorship_meta.status}` : ""}
                  </p>
                )}
                <p className="truncate text-xs text-muted-foreground">{c.last_message_preview || c.context_title}</p>
              </div>
            </button>
          ))
        ) : (
          <p className="p-6 text-center text-sm text-muted-foreground">No conversations yet.</p>
        )}
      </Card>

      {/* Active conversation */}
      <Card className={`flex flex-col overflow-hidden border-border bg-card ${id ? "flex" : "hidden md:flex"}`}>
        {!id ? (
          <div className="flex flex-1 flex-col items-center justify-center text-muted-foreground">
            <MessagesSquare className="mb-3 h-12 w-12" />
            <p>Select a conversation</p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 border-b border-border p-3">
              <button className="md:hidden" onClick={() => navigate("/messages")}><ArrowLeft className="h-5 w-5" /></button>
              <Avatar className="h-8 w-8"><AvatarFallback className="bg-secondary text-xs">{(convo ? otherName(convo) : "C").slice(0, 2).toUpperCase()}</AvatarFallback></Avatar>
              <div>
                <div className="flex items-center gap-1.5">
                  <p className="font-medium">{convo ? otherName(convo) : ""}</p>
                  {convo?.context_type === "sponsorship" && (
                    <Badge className="border-0 bg-primary/20 px-1.5 py-0 text-[10px] text-primary">Sponsorship</Badge>
                  )}
                </div>
                {convo?.context_type === "sponsorship" && convo?.sponsorship_meta ? (
                  <p className="text-xs text-muted-foreground">
                    @{convo.sponsorship_meta.username}
                    {convo.sponsorship_meta.tier ? ` · ${convo.sponsorship_meta.tier} tier` : ""}
                    {convo.sponsorship_meta.status ? ` · ${convo.sponsorship_meta.status}` : ""}
                  </p>
                ) : (
                  convo?.context_title && <p className="text-xs text-muted-foreground">Re: {convo.context_title}</p>
                )}
              </div>
            </div>
            <div className="flex-1 space-y-3 overflow-y-auto p-4">
              {loadingConvo ? (
                <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
              ) : (
                messages.map((m) => {
                  const mine = m.sender_id === user?.id;
                  if (m.message_type === "offer" && m.offer_id && offersById[m.offer_id]) {
                    const offer = offersById[m.offer_id];
                    const txn = txByOfferId[offer.id];
                    return (
                      <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                        <OfferMessageCard
                          offer={offer}
                          transaction={txn}
                          currentUserId={user?.id}
                          mine={mine}
                          onChanged={refreshAfterOfferChange}
                        />
                      </div>
                    );
                  }
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm ${mine ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>
                        {m.content}
                        <span className="mt-1 block text-[10px] opacity-70">{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={endRef} />
            </div>
            <div className="flex items-center gap-2 border-t border-border p-3">
              {isServiceSeller && (
                <Button variant="outline" size="sm" onClick={() => setQuoteOpen(true)} className="shrink-0" data-testid="send-quote-button">
                  <FileText className="mr-1.5 h-4 w-4" /> Quote
                </Button>
              )}
              <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type a message..." data-testid="message-input" />
              <Button size="icon" onClick={send} disabled={sending} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="message-send-button">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
          </>
        )}
      </Card>

      {/* Seller: send quote dialog */}
      <Dialog open={quoteOpen} onOpenChange={setQuoteOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Send a quote</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Quote for &ldquo;{serviceListing?.title}&rdquo;. The buyer can pay this securely on-platform.</p>
          <div>
            <Label htmlFor="quote-amount">Amount (USD)</Label>
            <Input id="quote-amount" type="number" min="0" step="0.01" value={quoteAmount} onChange={(e) => setQuoteAmount(e.target.value)} className="mt-1" data-testid="quote-amount-input" />
          </div>
          <div>
            <Label htmlFor="quote-msg">Message (optional)</Label>
            <Textarea id="quote-msg" rows={3} value={quoteMsg} onChange={(e) => setQuoteMsg(e.target.value)} className="mt-1" placeholder="Scope, turnaround, what's included..." data-testid="quote-message-input" />
          </div>
          <div>
            <Label>Attach a file (optional)</Label>
            {quoteFile ? (
              <div className="mt-1 flex items-center justify-between rounded-md border border-border p-2 text-sm">
                <span className="flex items-center gap-2 truncate"><Paperclip className="h-4 w-4" /> {quoteFile.name}</span>
                <button onClick={() => setQuoteFile(null)}><X className="h-4 w-4" /></button>
              </div>
            ) : (
              <label className="mt-1 flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-border p-2 text-sm text-muted-foreground hover:border-primary">
                <Paperclip className="h-4 w-4" /> Choose a document or image
                <input type="file" className="hidden" onChange={(e) => setQuoteFile(e.target.files?.[0] || null)} data-testid="quote-file-input" />
              </label>
            )}
          </div>
          <DialogFooter>
            <Button onClick={sendQuote} disabled={quoteBusy} className="bg-primary text-primary-foreground" data-testid="quote-submit-button">
              {quoteBusy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Send quote
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
